import { useEffect, useState } from "react";
import api from "../utils/api";

export default function PositionsAndOrdersCard({ userId, mode }) {
    // Take mode from parent, fall back to localStorage once if not provided
    const effectiveMode =
        (mode || localStorage.getItem("trade_mode") || "paper")
            .toLowerCase() === "live"
            ? "live"
            : "paper";

    const [alpacaPositions, setAlpacaPositions] = useState([]);
    const [alpacaOrders, setAlpacaOrders] = useState([]);
    const [alpacaError, setAlpacaError] = useState("");

    const [tradierPositions, setTradierPositions] = useState([]);
    const [tradierOrders, setTradierOrders] = useState([]);
    const [tradierError, setTradierError] = useState("");

    const safeArray = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) return val;
        return [val];
    };


    function isLikelyOCCOptionSymbol(sym) {
        const s = String(sym || "").trim().toUpperCase();
        // root + YYMMDD + C/P + strike (digits)
        // ex: NVDA251212C00192500
        return /^[A-Z]{1,10}\d{6}[CP]\d{8}$/.test(s);
    }

    function occExpirationDate(sym) {
        const s = String(sym || "").trim().toUpperCase();
        if (!isLikelyOCCOptionSymbol(s)) return null;

        // Root is leading letters; expiration YYMMDD starts right after root
        let i = 0;
        while (i < s.length && /[A-Z]/.test(s[i])) i++;

        const yy = parseInt(s.slice(i, i + 2), 10);
        const mm = parseInt(s.slice(i + 2, i + 4), 10);
        const dd = parseInt(s.slice(i + 4, i + 6), 10);
        if (!yy || !mm || !dd) return null;

        // 20YY is fine for modern options
        return new Date(Date.UTC(2000 + yy, mm - 1, dd));
    }

    function calcDTE(expDate) {
        if (!expDate) return null;
        const now = new Date();
        // compare in UTC days to avoid timezone weirdness
        const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
        const expUTC = Date.UTC(expDate.getUTCFullYear(), expDate.getUTCMonth(), expDate.getUTCDate());
        return Math.ceil((expUTC - todayUTC) / (1000 * 60 * 60 * 24));
    }

    function dteBadgeMeta(dte) {
        if (dte == null) return null;

        if (dte <= 0) return { label: `DTE: ${dte} (expires)`, cls: "bg-red-900/60 border-red-500/50 text-red-200" };
        if (dte <= 2) return { label: `DTE: ${dte} (very high decay)`, cls: "bg-red-900/40 border-red-500/40 text-red-200" };
        if (dte <= 7) return { label: `DTE: ${dte} (high decay)`, cls: "bg-orange-900/40 border-orange-400/40 text-orange-200" };
        if (dte <= 14) return { label: `DTE: ${dte} (medium decay)`, cls: "bg-yellow-900/30 border-yellow-400/40 text-yellow-200" };
        return { label: `DTE: ${dte} (low decay)`, cls: "bg-emerald-900/30 border-emerald-400/40 text-emerald-200" };
    }


    // ================== ALPACA ==================

    // initial load (positions + orders) whenever user or mode changes
    useEffect(() => {
        if (!userId) return;

        const load = async () => {
            try {
                const posRes = await api.get("/api/brokers/alpaca/positions", {
                    params: { user_id: userId, mode: effectiveMode },
                });
                const raw = posRes.data;
                const arr = Array.isArray(raw) ? raw : safeArray(raw);
                setAlpacaPositions(arr);
                setAlpacaError("");
            } catch (e) {
                setAlpacaError("Could not load positions");
                setAlpacaPositions([]);
            }

            try {
                const ordRes = await api.get("/api/brokers/alpaca/orders", {
                    params: { user_id: userId, mode: effectiveMode },
                });
                const rawO = ordRes.data;
                const arrO = Array.isArray(rawO) ? rawO : safeArray(rawO);
                setAlpacaOrders(arrO);
            } catch {
                // ignore
            }
        };

        load();
    }, [userId, effectiveMode]);

    // poll Alpaca orders
    useEffect(() => {
        if (!userId) return;

        const fetchOrders = () => {
            api.get("/api/brokers/alpaca/orders", {
                params: { user_id: userId, mode: effectiveMode },
            })
                .then((r) => {
                    const raw = r.data;
                    const arr = Array.isArray(raw) ? raw : safeArray(raw);
                    setAlpacaOrders(arr);
                })
                .catch(() => { });
        };

        const t = setInterval(fetchOrders, 5000);
        return () => clearInterval(t);
    }, [userId, effectiveMode]);

    const closeAlpacaPosition = async (symbol) => {
        if (!window.confirm(`Close Alpaca position ${symbol}?`)) return;
        try {
            await api.post("/api/trade/close/equity", {
                user_id: userId,
                symbol,
                mode: effectiveMode,
            });
            // refresh
            const posRes = await api.get("/api/brokers/alpaca/positions", {
                params: { user_id: userId, mode: effectiveMode },
            });
            const raw = posRes.data;
            const arr = Array.isArray(raw) ? raw : safeArray(raw);
            setAlpacaPositions(arr);
        } catch (e) {
            alert(
                e?.response?.data?.error ||
                e?.response?.data?.detail ||
                e?.message ||
                "Close equity position failed."
            );
        }
    };

    // ================== TRADIER ==================

    // initial load
    useEffect(() => {
        if (!userId) return;

        const load = async () => {
            // positions
            try {
                const { data } = await api.get("/api/tradier/positions", {
                    params: { user_id: userId, mode: effectiveMode },
                });

                if (data && data.ok === false && data.error) {
                    setTradierError(data.detail || data.error);
                    setTradierPositions([]);
                } else {
                    const arr = safeArray(
                        data.positions ||
                        (data.positions && data.positions.position)
                    );
                    setTradierPositions(
                        arr.filter((p) => p && typeof p === "object")
                    );
                    setTradierError("");
                }
            } catch (e) {
                const apiErr = e?.response?.data;
                const msg =
                    apiErr?.detail ||
                    apiErr?.error ||
                    e?.message ||
                    "Could not load Tradier positions.";
                setTradierError(msg);
                setTradierPositions([]);
            }

            // orders
            try {
                const { data } = await api.get("/api/brokers/tradier/orders", {
                    params: { user_id: userId, mode: effectiveMode },
                });
                const arrO = safeArray(data.orders || data.order || data);
                setTradierOrders(
                    arrO.filter((o) => o && typeof o === "object")
                );
            } catch {
                // ignore
            }
        };

        load();
    }, [userId, effectiveMode]);

    // poll Tradier orders
    useEffect(() => {
        if (!userId) return;

        const fetchOrders = () => {
            api.get("/api/brokers/tradier/orders", {
                params: { user_id: userId, mode: effectiveMode },
            })
                .then((resO) => {
                    const dataO = resO.data || {};
                    const arrO = safeArray(
                        dataO.orders || dataO.order || dataO
                    );
                    setTradierOrders(
                        arrO.filter((o) => o && typeof o === "object")
                    );
                })
                .catch(() => { });
        };

        const t = setInterval(fetchOrders, 10000);
        return () => clearInterval(t);
    }, [userId, effectiveMode]);

    const closeTradierPosition = async (optionSymbol) => {
        if (!window.confirm(`Close Tradier option ${optionSymbol}?`)) return;
        try {
            await api.post("/api/trade/close/option", {
                user_id: userId,
                option_symbol: optionSymbol,
                mode: effectiveMode,
            });
            // refresh positions
            const { data } = await api.get("/api/tradier/positions", {
                params: { user_id: userId, mode: effectiveMode },
            });
            const arr = safeArray(
                data.positions || (data.positions && data.positions.position)
            );
            setTradierPositions(arr.filter((p) => p && typeof p === "object"));
        } catch (e) {
            console.error("closeTradierPosition error", e?.response?.data || e);
            alert(
                e?.response?.data?.error ||
                e?.response?.data?.detail ||
                e?.message ||
                "Could not send close order to Tradier."
            );
        }
    };

    // ================== render helpers ==================

    const renderAlpacaPositions = () => {
        if (alpacaPositions.length === 0) {
            return (
                <div className="text-sm text-zinc-400">
                    No open Alpaca positions
                </div>
            );
        }
        return (
            <div className="space-y-2 text-sm text-zinc-200">
                {alpacaPositions.map((p) => (
                    <div
                        key={p.symbol}
                        className="flex justify-between bg-zinc-800 rounded px-3 py-2"
                    >
                        <div>
                            <div className="font-semibold">{p.symbol}</div>
                            <div className="text-xs text-zinc-400">
                                Avg: {p.avg_entry_price} · Qty: {p.qty}
                            </div>
                        </div>

                        <div className="text-right">
                            <div>Current: {p.current_price}</div>
                            <div
                                className={
                                    Number(p.unrealized_pl) >= 0
                                        ? "text-emerald-400 text-xs"
                                        : "text-red-400 text-xs"
                                }
                            >
                                P/L: {p.unrealized_pl} (
                                {p.unrealized_plpc
                                    ? `${p.unrealized_plpc}%`
                                    : "—"}
                                )
                            </div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    closeAlpacaPosition(p.symbol);
                                }}
                                className="mt-1 px-2 py-1 text-xs rounded bg-red-600 hover:bg-red-500 text-white"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderAlpacaOrders = () => {
        if (alpacaOrders.length === 0) {
            return (
                <div className="text-sm text-zinc-400">
                    No recent Alpaca orders
                </div>
            );
        }
        return (
            <div className="space-y-2 text-sm text-zinc-200">
                {alpacaOrders.slice(0, 5).map((o) => (
                    <div
                        key={o.id}
                        className="flex justify-between bg-zinc-800 rounded px-3 py-2"
                    >
                        <div>
                            <div className="font-semibold">
                                {o.symbol} · {o.side}
                            </div>
                            <div className="text-xs text-zinc-400">
                                {o.qty} @{" "}
                                {o.limit_price ||
                                    o.filled_avg_price ||
                                    "mkt"}
                            </div>
                        </div>
                        <div className="text-right text-xs text-zinc-400">
                            {o.status}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderTradierPositions = () => {
        if (tradierError) {
            return (
                <div className="text-sm text-red-400">
                    {tradierError}
                </div>
            );
        }
        if (tradierPositions.length === 0) {
            return (
                <div className="text-sm text-zinc-400">
                    No open Tradier positions
                </div>
            );
        }

        return (
            <div className="space-y-2 text-sm text-zinc-200">
                {tradierPositions.map((p, idx) => {
                    const sym = p.symbol || p.underlying_symbol || "?";
                    const qtyRaw = p.quantity ?? p.qty ?? p.size ?? 0;
                    const qtyNum = Number(qtyRaw) || 0;
                    const lastPrice =
                        Number(
                            p.last ??
                            p.last_price ??
                            p.close_price ??
                            p.current_price ??
                            p.price ??
                            p.mark ??
                            0
                        ) || 0;

                    const costBasis =
                        Number(
                            p.cost_basis || p.cost_basis_price || 0
                        ) || 0;

                    const assetType = String(p.asset_type || "").toLowerCase();
                    const isEquity = assetType === "equity" || assetType === "stock";

                    const multiplier = isEquity ? 1 : 100;

                    const expectedCurrentValue =
                        qtyNum > 0 && lastPrice > 0
                            ? Math.abs(qtyNum) * lastPrice * multiplier
                            : 0;

                    const rawMarketValue = Number(p.market_value || 0) || 0;

                    /**
                     * Tradier can sometimes return market_value as cents.
                     * Example:
                     *   6101238 should display as $61,012.38
                     *   not $6,101,238.00
                     */
                    function normalizeTradierMoney(rawValue, expectedValue, basisValue) {
                        let v = Number(rawValue || 0);

                        if (!v) return 0;

                        // Best check: compare broker value to qty * last * multiplier
                        if (expectedValue > 0 && v > expectedValue * 20) {
                            return v / 100;
                        }

                        // Backup check: compare broker value to cost basis
                        if (basisValue > 0 && v > basisValue * 20) {
                            return v / 100;
                        }

                        // Last safety fallback for obvious cents-style values
                        if (v > 1_000_000 && expectedValue > 0 && v > expectedValue * 5) {
                            return v / 100;
                        }

                        return v;
                    }

                    const marketValue = rawMarketValue
                        ? normalizeTradierMoney(rawMarketValue, expectedCurrentValue, costBasis)
                        : expectedCurrentValue;

                    const currentValue = marketValue || expectedCurrentValue || null;

                    let pl = null;
                    let plPct = null;

                    if (costBasis > 0 && currentValue > 0) {
                        pl = currentValue - costBasis;
                        plPct = (pl / costBasis) * 100;
                    }

                    const plClass =
                        pl != null && pl >= 0
                            ? "text-emerald-400"
                            : "text-red-400";

                    return (
                        <div
                            key={p.id || sym || idx}
                            className="flex justify-between bg-zinc-800 rounded px-3 py-2"
                        >
                            <div>
                                <div className="font-medium flex items-center gap-2 flex-wrap">
                                    <span>{sym}</span>

                                    {isLikelyOCCOptionSymbol(sym) && (() => {
                                        const exp = occExpirationDate(sym);
                                        const dte = calcDTE(exp);
                                        const meta = dteBadgeMeta(dte);
                                        if (!meta) return null;

                                        return (
                                            <span
                                                className={`inline-flex items-center px-2 py-[2px] rounded-full border text-[10px] ${meta.cls}`}
                                                title={exp ? `Expiration: ${exp.toISOString().slice(0, 10)}` : ""}
                                            >
                                                {meta.label}
                                            </span>
                                        );
                                    })()}

                                    {p.description ? (
                                        <span className="text-xs text-zinc-400">· {p.description}</span>
                                    ) : null}
                                </div>

                                <div className="text-xs text-zinc-400">
                                    Qty: {qtyRaw} · Cost:{" "}
                                    {costBasis ||
                                        p.cost_basis_price ||
                                        "-"}{" "}
                                    · Last: {lastPrice || "-"}
                                </div>
                            </div>

                            <div className="text-right text-xs text-zinc-400">
                                {currentValue != null && (
                                    <div>
                                        Current:{" "}
                                        ${currentValue.toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })}
                                    </div>
                                )}
                                {pl != null && plPct != null && (
                                    <div className={plClass}>
                                        P/L: ${pl.toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })} (
                                        {plPct.toFixed(2)}%)
                                    </div>
                                )}
                                <div className="mt-1">
                                    <button
                                        onClick={() =>
                                            closeTradierPosition(sym)
                                        }
                                        className="px-2 py-1 rounded bg-red-600 hover:bg-red-500 text-white"
                                    >
                                        Close
                                    </button>
                                </div>
                                <div className="mt-1 text-[10px] text-zinc-500">
                                    {p.open_date ||
                                        p.date_acquired ||
                                        ""}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderTradierOrders = () => {
        if (tradierOrders.length === 0) {
            return (
                <div className="text-sm text-zinc-400">
                    No recent Tradier orders
                </div>
            );
        }
        return (
            <div className="space-y-2 text-sm text-zinc-200">
                {tradierOrders.slice(0, 5).map((o, idx) => (
                    <div
                        key={o.id || idx}
                        className="flex justify-between bg-zinc-800 rounded px-3 py-2"
                    >
                        <div>
                            <div className="font-semibold">
                                {(o.symbol || o.underlying) ?? "?"} ·{" "}
                                {(o.side ||
                                    o.transaction_type ||
                                    "")?.toLowerCase()}
                            </div>
                            <div className="text-xs text-zinc-400">
                                {o.quantity || o.qty || ""} @{" "}
                                {o.price ||
                                    o.avg_fill_price ||
                                    "mkt"}
                            </div>
                        </div>
                        <div className="text-right text-xs text-zinc-400">
                            {o.status || o.order_status || ""} <br />
                            {o.create_date ||
                                o.transaction_date ||
                                ""}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // ================== render ==================

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-4">
            <h3 className="text-lg font-semibold">
                Your Positions (Alpaca – {effectiveMode.toUpperCase()})
            </h3>
            {alpacaError && (
                <div className="text-sm text-red-400 mb-1">
                    {alpacaError}
                </div>
            )}
            {renderAlpacaPositions()}

            <h3 className="text-lg font-semibold pt-4 border-t border-zinc-700">
                Alpaca Recent Orders
            </h3>
            {renderAlpacaOrders()}

            <h3 className="text-lg font-semibold pt-4 border-t border-zinc-700">
                Your Positions (Tradier – {effectiveMode.toUpperCase()})
            </h3>
            {renderTradierPositions()}

            <h3 className="text-lg font-semibold pt-4 border-t border-zinc-700">
                Tradier Recent Orders
            </h3>
            {renderTradierOrders()}
        </div>
    );
}
